import React from "react";

/**
 * A tiny render-crash boundary. Any `throw` inside its subtree is
 * caught and rendered as an inline error card instead of taking the
 * whole page to white. Add ONE per route that has ever gone blank on
 * users. The captured error/stack is dumped to the console so you can
 * copy-paste it into a bug report.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log so ops can see it in the browser console AND in any error
    // reporter the app installs downstream.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.name || "?", error, info);
    this.setState({ info });
  }

  handleReset = () => this.setState({ error: null, info: null });

  render() {
    if (!this.state.error) return this.props.children;

    const err = this.state.error;
    return (
      <div
        className="max-w-3xl mx-auto py-10 px-4"
        data-testid={`error-boundary-${this.props.name || "root"}`}
      >
        <div className="rounded-xl border-2 border-red-400 bg-red-50 dark:bg-red-950/40 p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className="text-2xl leading-none">⚠️</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-red-900 dark:text-red-200">
                Something on this page threw an error.
              </div>
              <p className="text-sm text-red-800 dark:text-red-300 mt-1">
                The rest of the app is fine - this section just crashed while
                rendering. Please copy the details below into a bug report.
              </p>
            </div>
          </div>
          <div className="rounded-md border border-red-300 dark:border-red-800 bg-white/70 dark:bg-black/30 p-3 text-xs font-mono break-all">
            <div className="font-semibold mb-1 text-red-800 dark:text-red-300">
              {err?.name || "Error"}: {err?.message || String(err)}
            </div>
            {err?.stack && (
              <pre className="whitespace-pre-wrap text-[10px] leading-relaxed max-h-48 overflow-auto opacity-80">
                {err.stack}
              </pre>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={this.handleReset}
              className="text-xs px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white font-medium"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="text-xs px-3 py-1.5 rounded-md border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 font-medium"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
